import React from 'react';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {prism} from 'react-syntax-highlighter/dist/esm/styles/prism';

const MemorizeConversation = ({ onSectionChange }) => {
  return (
    <div className="api-reference-layout">
      <div className="api-content">
        <div className="api-header">
          <div className="api-method-badge post">POST</div>
          <h1>memorize_conversation - Memorize Conversation</h1>
          <p className="api-description">
            Start an asynchronous task to memorize and process conversation content
          </p>
        </div>

        <div className="api-endpoint">
          <div className="endpoint-url">
            <span className="method post">POST</span>
            <span className="url">/api/v2/memory/memorize</span>
          </div>
        </div>

        <div className="api-section">
          <h2>Authorization</h2>
          <div className="auth-item">
            <div className="auth-header">
              <span className="auth-name">Authorization</span>
              <span className="auth-type">string</span>
              <span className="auth-location">header</span>
              <span className="required-badge">required</span>
            </div>
            <p>Bearer authentication header of the form Bearer &lt;token&gt;, where &lt;token&gt; is your API key.</p>
          </div>
        </div>

        <div className="api-section">
          <h2>Request Body</h2>
          <div className="content-type">application/json</div>

          <div className="params-container">
            <div className="param-item">
              <div className="param-header">
                <span className="param-name">conversation_text</span>
                <span className="param-type">string</span>
                <span className="optional-badge">optional*</span>
              </div>
              <p>Plain text format conversation content</p>
              <div className="param-example">
                <strong>Example:</strong>
                <code>"user: Hello!\nassistant: Hi there! How can I help you today?"</code>
              </div>
            </div>

            <div className="param-divider"></div>

            <div className="param-item">
              <div className="param-header">
                <span className="param-name">conversation</span>
                <span className="param-type">array[object]</span>
                <span className="optional-badge">optional*</span>
              </div>
              <p>Structured message format conversation content</p>
              <div className="param-example">
                <strong>Example:</strong>
                <code>{`[{"role": "user", "content": "Hello!", "time": "2024-01-01T10:00:00Z"}]`}</code>
              </div>
            </div>

            <div className="param-divider"></div>

            <div className="param-item">
              <div className="param-header">
                <span className="param-name">user_id</span>
                <span className="param-type">string</span>
                <span className="required-badge">required</span>
              </div>
              <p>Unique identifier for the user</p>
              <div className="param-example">
                <strong>Example:</strong>
                <code>"user001"</code>
              </div>
            </div>

            <div className="param-divider"></div>

            <div className="param-item">
              <div className="param-header">
                <span className="param-name">user_name</span>
                <span className="param-type">string</span>
                <span className="required-badge">required</span>
              </div>
              <p>Display name for the user</p>
              <div className="param-example">
                <strong>Example:</strong>
                <code>"John Doe"</code>
              </div>
            </div>

            <div className="param-divider"></div>

            <div className="param-item">
              <div className="param-header">
                <span className="param-name">agent_id</span>
                <span className="param-type">string</span>
                <span className="required-badge">required</span>
              </div>
              <p>Unique identifier for the agent</p>
              <div className="param-example">
                <strong>Example:</strong>
                <code>"agent001"</code>
              </div>
            </div>

            <div className="param-divider"></div>

            <div className="param-item">
              <div className="param-header">
                <span className="param-name">agent_name</span>
                <span className="param-type">string</span>
                <span className="required-badge">required</span>
              </div>
              <p>Display name for the agent</p>
              <div className="param-example">
                <strong>Example:</strong>
                <code>"AI Assistant"</code>
              </div>
            </div>

            <div className="param-divider"></div>

            <div className="param-item">
              <div className="param-header">
                <span className="param-name">session_date</span>
                <span className="param-type">string</span>
                <span className="optional-badge">optional</span>
              </div>
              <p>Session date in ISO 8601 format (default: current time)</p>
              <div className="param-example">
                <strong>Example:</strong>
                <code>"2024-01-01T10:00:00Z"</code>
              </div>
            </div>
          </div>

          <div className="api-note">
            <p><strong>Note:</strong> Either conversation_text or conversation is required</p>
          </div>

          <div className="conversation-structure">
            <h3>conversation structure (when using structured format):</h3>
            <div className="param-example">
              <code>{`[
  {
    "role": "user",           // Message role (user/assistant/system)
    "content": "Hello!",      // Message content
  }
]`}</code>
            </div>
          </div>
        </div>

        <div className="api-section">
          <h2>Response</h2>
          <div className="response-status">
            <span className="status-code success">200</span>
            <span className="content-type">application/json</span>
          </div>

          <div className="params-container">
            <div className="param-item">
              <div className="param-header">
                <span className="param-name">task_id</span>
                <span className="param-type">string</span>
              </div>
              <p>Unique identifier for the task, used to track processing status</p>
            </div>

            <div className="param-divider"></div>

            <div className="param-item">
              <div className="param-header">
                <span className="param-name">status</span>
                <span className="param-type">string</span>
              </div>
              <p>Task status (usually "PENDING")</p>
            </div>

            <div className="param-divider"></div>

            <div className="param-item">
              <div className="param-header">
                <span className="param-name">message</span>
                <span className="param-type">string</span>
              </div>
              <p>Response message</p>
            </div>
          </div>
        </div>
      </div>

      <div className="api-demo-sidebar">
        <div className="demo-sticky">
          <div className="demo-header">
            <div className="demo-tabs">
              <button className="demo-tab active">Python</button>
              <button className="demo-tab">JavaScript</button>
              <button className="demo-tab">cURL</button>
            </div>
          </div>

           <div className="demo-content">
              <SyntaxHighlighter
                  language="python"
                  style={prism}
                  customStyle={{
                    margin: 0,
                    padding: '20px',
                    background: 'white',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    borderRadius: '0 0 8px 8px',
                    border: '1px solid #e5e7eb'
                  }}
              >
                {`import os
memu_client = MemuClient(
    base_url="https://api.memu.so",
    api_key=os.getenv("MEMU_API_KEY")
)

# Build context
context = []
context.append({"role": "user", "content": "Hi. Got any fun plans for the weekend?"})
context.append({"role": "assistant", "content": "Not sure yet! Maybe just relax. You?"})
context.append({"role": "user", "content": "Thinking of a movie. Any good ones out?"})
context.append({"role": "assistant", "content": "Yes! The new sci-fi one is getting great reviews."})
# Send request
response = memu_client.memorize_conversation(
    conversation=context,
    user_id="user001",
    user_name="Demo User",
    agent_id="gpt001",
    agent_name="GPT Assistant"
)

print(f"💾 Saved! Task ID: {memo_response.task_id}")`}
            </SyntaxHighlighter>
          </div>

          <div className="response-section">
            <div className="response-header">
              <span className="response-status-code">200</span>
              <span>Response</span>
            </div>
             <SyntaxHighlighter
               language="json"
               style={prism}
               customStyle={{
                 margin: 0,
                 padding: '20px',
                 background: 'white',
                 fontSize: '14px',
                 lineHeight: '1.5',
                 borderRadius: '0 0 8px 8px',
                 border: '1px solid #e5e7eb'
               }}
             >
{`{
  "task_id": "task_abc123def456",
  "status": "PENDING",
}`}
            </SyntaxHighlighter>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemorizeConversation;

